// Daten für „Zur Ablage“ (Handoff Dokumente §6.3): Vorschläge je Segment mit Anhang, E-Mail, Analyse, Regel, Objekt.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase/client';
import { alleSeiten } from '../supabase/alleSeiten';
import { MASSEN_ABLAGE_SCHWELLE } from './kennzahlen';
import { SEGMENT_STATUS, fuerMassenablage, type AblageSegment } from './ablage';

export interface AblageZeile {
  id: string; status: string; confidence: number; objekt_id: string | null; ziel_pfad: string | null; dateiname: string;
  dokument_id: string | null; gdrive_file_id: string | null; created_at: string; entschieden_at: string | null; fehler: string | null;
  anhang: { id: string; dateiname: string | null; dateigroesse: number | null; dateityp: string | null; graph_message_id: string | null; graph_attachment_id: string | null } | null;
  email: {
    id: string; von: string | null; von_name: string | null; betreff: string | null; datum: string | null;
    analyse: { absender_typ: string | null; zusammenfassung: string | null } | null;
  } | null;
  regel: { name: string | null } | null;
}

const AUSWAHL = `id, status, confidence, objekt_id, ziel_pfad, dateiname, dokument_id, gdrive_file_id, created_at, entschieden_at, fehler,
  anhang:anhang_id(id, dateiname, dateigroesse, dateityp, graph_message_id, graph_attachment_id),
  email:email_id(id, von, von_name, betreff, datum, analyse:email_analysen(absender_typ, zusammenfassung)),
  regel:regel_id(name)`;

const SEITE = 20;

/** Analyse ist 1:1 (UNIQUE email_id), PostgREST liefert je nach Erkennung Objekt oder Liste. */
function normalisiere(roh: unknown[]): AblageZeile[] {
  return roh.map(r => {
    const z = r as AblageZeile & { email: (AblageZeile['email'] & { analyse: unknown }) | null };
    const a = z.email?.analyse;
    return { ...z, confidence: Number(z.confidence), email: z.email ? { ...z.email, analyse: (Array.isArray(a) ? a[0] : a) ?? null } : null } as AblageZeile;
  });
}

export function useZurAblage(segment: AblageSegment) {
  const [zaehler, setZaehler] = useState<Record<AblageSegment, number> | null>(null);
  const [massen, setMassen] = useState(0);
  const [zeilen, setZeilen] = useState<AblageZeile[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [mehr, setMehr] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [runde, setRunde] = useState(0);
  const token = useRef(0);

  const ladeZaehler = useCallback(async () => {
    const zahl = (s: AblageSegment) => supabase.from('dokument_ablage_vorschlaege').select('id', { count: 'exact', head: true }).in('status', [...SEGMENT_STATUS[s]]);
    const [o, g, a, m] = await Promise.all([
      zahl('offen'), zahl('abgelegt'), zahl('abgelehnt'),
      supabase.from('dokument_ablage_vorschlaege').select('id, anhang:anhang_id!inner(graph_message_id, graph_attachment_id)', { count: 'exact', head: true })
        .in('status', [...SEGMENT_STATUS.offen]).gte('confidence', MASSEN_ABLAGE_SCHWELLE)
        .not('objekt_id', 'is', null).not('ziel_pfad', 'is', null).neq('ziel_pfad', '').neq('dateiname', '')
        .not('anhang.graph_message_id', 'is', null).not('anhang.graph_attachment_id', 'is', null),
    ]);
    setZaehler({ offen: o.count ?? 0, abgelegt: g.count ?? 0, abgelehnt: a.count ?? 0 });
    setMassen(m.count ?? 0);
  }, []);

  const ladeSeite = useCallback(async (von: number): Promise<AblageZeile[]> => {
    const sortierung = segment === 'offen' ? 'created_at' : 'entschieden_at';
    const { data, error } = await supabase.from('dokument_ablage_vorschlaege').select(AUSWAHL)
      .in('status', [...SEGMENT_STATUS[segment]])
      .order(sortierung, { ascending: false, nullsFirst: false }).order('id')
      .range(von, von + SEITE - 1);
    if (error) throw new Error(error.message);
    return normalisiere(data ?? []);
  }, [segment]);

  useEffect(() => {
    const t = ++token.current;
    setLaedt(true); setFehler(null);
    Promise.all([ladeSeite(0), ladeZaehler()])
      .then(([z]) => { if (t === token.current) { setZeilen(z); setMehr(z.length === SEITE); } })
      .catch((e: unknown) => { if (t === token.current) setFehler(e instanceof Error ? e.message : 'Vorschläge nicht geladen'); })
      .finally(() => { if (t === token.current) setLaedt(false); });
  }, [ladeSeite, ladeZaehler, runde]);

  const ladeMehr = useCallback(async () => {
    const t = token.current;
    try {
      const z = await ladeSeite(zeilen.length);
      if (t !== token.current) return;
      setZeilen(alt => [...alt, ...z.filter(n => !alt.some(a => a.id === n.id))]);
      setMehr(z.length === SEITE);
    } catch (e) { setFehler(e instanceof Error ? e.message : 'Weitere Vorschläge nicht geladen'); }
  }, [ladeSeite, zeilen.length]);

  /** Karte nach Entscheidung aus dem Segment nehmen (ohne die Liste neu zu laden) und Zähler auffrischen. */
  const entferne = useCallback((id: string) => {
    setZeilen(alt => alt.filter(z => z.id !== id));
    void ladeZaehler();
  }, [ladeZaehler]);

  return { zaehler, massen, zeilen, laedt, mehr, fehler, ladeMehr, entferne, neuLaden: () => setRunde(r => r + 1) };
}

/** Alle Kandidaten für „Alle ≥ 90 % ablegen“ (vollständig, hohe Sicherheit). */
export async function ladeMassenKandidaten(): Promise<AblageZeile[]> {
  const roh = await alleSeiten<unknown>((von, bis) => supabase.from('dokument_ablage_vorschlaege').select(AUSWAHL)
    .in('status', [...SEGMENT_STATUS.offen]).gte('confidence', MASSEN_ABLAGE_SCHWELLE).not('objekt_id', 'is', null)
    .order('created_at').order('id').range(von, bis));
  return normalisiere(roh).filter(z => fuerMassenablage({
    confidence: z.confidence, objekt_id: z.objekt_id, ziel_pfad: z.ziel_pfad, dateiname: z.dateiname,
    graph_message_id: z.anhang?.graph_message_id ?? null, graph_attachment_id: z.anhang?.graph_attachment_id ?? null,
  }));
}
